import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container, Typography, Grid, Paper, Chip, Button, TextField, Box, Alert,
  Card, CardContent, Divider, Avatar, InputAdornment, Skeleton, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import {
  Gavel, AutoAwesome, Visibility, CalendarToday, LocationOn,
  Category, ArrowForward, Inventory2, Share, Favorite, FavoriteBorder,
  ShoppingCart, Chat, LocalShipping, Star, Send
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { inventoryAPI, transactionAPI, aiAPI, wishlistAPI, cartAPI, messageAPI } from '../services/api';

const MotionPaper = motion(Paper);

const ItemDetailPage = () => {
  const { id } = useParams();
  const { user, isAuthenticated } = useAuth();
  const [item, setItem] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [offerPrice, setOfferPrice] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [negotiation, setNegotiation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inWishlist, setInWishlist] = useState(false);
  const [messageDialog, setMessageDialog] = useState(false);
  const [messageText, setMessageText] = useState('');

  useEffect(() => {
    loadItem();
  }, [id]);

  const loadItem = async () => {
    setLoading(true);
    try {
      const response = await inventoryAPI.getItem(id);
      setItem(response.data);
      if (isAuthenticated) {
        try {
          const wishRes = await wishlistAPI.check(id);
          setInWishlist(wishRes.data.in_wishlist);
        } catch (e) {}
      }
      try {
        const recsResponse = await aiAPI.recommendations(id);
        setRecommendations(recsResponse.data || []);
      } catch (e) {}
    } catch (err) {
      console.error('Failed to load item', err);
    } finally {
      setLoading(false);
    }
  };

  const handleWishlist = async () => {
    if (!isAuthenticated) { toast.error('Login first'); return; }
    try {
      if (inWishlist) {
        await wishlistAPI.remove(id);
        setInWishlist(false);
        toast.success('Removed from wishlist');
      } else {
        await wishlistAPI.add(id);
        setInWishlist(true);
        toast.success('Added to wishlist');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleAddToCart = async () => {
    if (!isAuthenticated) { toast.error('Login first'); return; }
    try {
      await cartAPI.add({ item_id: parseInt(id), quantity });
      toast.success('Added to cart');
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const handleOffer = async () => {
    if (!offerPrice) { toast.error('Enter an offer price'); return; }
    try {
      await transactionAPI.create({
        item_id: parseInt(id), quantity, offered_price: parseFloat(offerPrice),
      });
      toast.success('Order placed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to submit offer');
    }
  };

  const handleNegotiate = async () => {
    if (!offerPrice || !item) { toast.error('Enter your offer price first'); return; }
    try {
      const response = await aiAPI.negotiate({
        item_name: item.item_name,
        seller_price: item.selling_price,
        buyer_offer: parseFloat(offerPrice),
        item_condition: item.condition,
      });
      setNegotiation(response.data);
    } catch (err) {
      toast.error('Negotiation service unavailable');
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim()) return;
    try {
      // Get seller ID from item's company
      await messageAPI.send({
        receiver_id: item.company_id, // Note: this should be owner_id in real app
        item_id: parseInt(id),
        content: messageText,
      });
      toast.success('Message sent to seller');
      setMessageDialog(false);
      setMessageText('');
    } catch (err) {
      toast.error('Failed to send message');
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}><Skeleton variant="rounded" height={400} /></Grid>
          <Grid item xs={12} md={4}><Skeleton variant="rounded" height={300} /></Grid>
        </Grid>
      </Container>
    );
  }

  if (!item) {
    return (
      <Container sx={{ py: 8, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">Item not found</Typography>
      </Container>
    );
  }

  const discount = item.original_price
    ? Math.round((1 - item.selling_price / item.original_price) * 100) : null;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '80vh', py: 4 }}>
      <Container maxWidth="lg">
        <Grid container spacing={4}>
          {/* Left: Item Details */}
          <Grid item xs={12} md={8}>
            <MotionPaper
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              sx={{ p: 4, borderRadius: 4 }} elevation={0}
            >
              {/* Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h4" fontWeight={700} gutterBottom>
                    {item.item_name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Chip icon={<Category />} label={item.category} color="primary" variant="outlined" size="small" />
                    <Chip label={item.condition?.replace('_', ' ')} size="small"
                      color={item.condition === 'new' ? 'success' : 'default'} />
                    <Chip label={item.status} size="small"
                      color={item.status === 'active' ? 'success' : 'default'} variant="outlined" />
                    {item.brand && <Chip label={item.brand} size="small" variant="outlined" />}
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 0.5 }}>
                  <Tooltip title={inWishlist ? 'Remove from Wishlist' : 'Add to Wishlist'}>
                    <IconButton onClick={handleWishlist} color={inWishlist ? 'error' : 'default'}>
                      {inWishlist ? <Favorite /> : <FavoriteBorder />}
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Share">
                    <IconButton onClick={() => { navigator.clipboard.writeText(window.location.href); toast.success('Link copied!'); }}>
                      <Share />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* Price */}
              <Box sx={{ my: 3, p: 3, bgcolor: '#f0fdf4', borderRadius: 2, border: '1px solid #bbf7d0' }}>
                <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2 }}>
                  <Typography variant="h3" color="primary.main" fontWeight={700}>
                    ₹{item.selling_price?.toLocaleString()}
                  </Typography>
                  {item.original_price && (
                    <Typography variant="h6" color="text.disabled" sx={{ textDecoration: 'line-through' }}>
                      ₹{item.original_price?.toLocaleString()}
                    </Typography>
                  )}
                  {discount && <Chip label={`${discount}% OFF`} color="success" size="small" />}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  Per unit | Min order: {item.min_order_quantity} unit(s)
                </Typography>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Description */}
              <Typography variant="h6" fontWeight={600} gutterBottom>Description</Typography>
              <Typography variant="body1" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                {item.description || 'No description provided for this item.'}
              </Typography>

              {item.specifications && (
                <>
                  <Typography variant="h6" fontWeight={600} gutterBottom sx={{ mt: 2 }}>
                    Technical Specifications
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph sx={{ lineHeight: 1.8 }}>
                    {item.specifications}
                  </Typography>
                </>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Details Grid */}
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Inventory2 color="primary" />
                    <Typography variant="h6" fontWeight={600}>{item.quantity}</Typography>
                    <Typography variant="caption" color="text.secondary">Available</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <LocationOn color="primary" />
                    <Typography variant="h6" fontWeight={600}>{item.location || 'N/A'}</Typography>
                    <Typography variant="caption" color="text.secondary">Location</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <Visibility color="primary" />
                    <Typography variant="h6" fontWeight={600}>{item.views_count}</Typography>
                    <Typography variant="caption" color="text.secondary">Views</Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                    <CalendarToday color="primary" />
                    <Typography variant="h6" fontWeight={600}>
                      {new Date(item.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">Listed</Typography>
                  </Box>
                </Grid>
              </Grid>

              {/* Extra details */}
              {(item.warranty_months || item.model_number || item.weight_kg) && (
                <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>Additional Details</Typography>
                  <Grid container spacing={2}>
                    {item.model_number && (
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Model</Typography>
                        <Typography variant="body2" fontWeight={500}>{item.model_number}</Typography>
                      </Grid>
                    )}
                    {item.warranty_months && (
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Warranty</Typography>
                        <Typography variant="body2" fontWeight={500}>{item.warranty_months} months</Typography>
                      </Grid>
                    )}
                    {item.weight_kg && (
                      <Grid item xs={4}>
                        <Typography variant="caption" color="text.secondary">Weight</Typography>
                        <Typography variant="body2" fontWeight={500}>{item.weight_kg} kg</Typography>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              )}
            </MotionPaper>
          </Grid>

          {/* Right: Actions */}
          <Grid item xs={12} md={4}>
            <Box sx={{ position: 'sticky', top: 80 }}>
              {/* Quick Buy */}
              <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }} elevation={0}>
                <Typography variant="h6" fontWeight={600} gutterBottom>
                  Quick Buy
                </Typography>
                {isAuthenticated ? (
                  <>
                    <TextField
                      fullWidth label="Quantity" type="number" size="small" sx={{ mb: 2 }}
                      value={quantity} onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      inputProps={{ min: item.min_order_quantity, max: item.quantity }}
                    />
                    <Button
                      fullWidth variant="contained" size="large" sx={{ mb: 1 }}
                      startIcon={<ShoppingCart />} onClick={handleAddToCart}
                    >
                      Add to Cart — ₹{(item.selling_price * quantity).toLocaleString()}
                    </Button>
                    <Button
                      fullWidth variant="outlined" size="small"
                      startIcon={<Chat />} onClick={() => setMessageDialog(true)}
                    >
                      Message Seller
                    </Button>
                  </>
                ) : (
                  <Box sx={{ textAlign: 'center', py: 2 }}>
                    <Typography color="text.secondary" gutterBottom>Sign in to buy</Typography>
                    <Button variant="contained" component={Link} to="/login" size="small">Login</Button>
                  </Box>
                )}
              </Paper>

              {/* Make Offer */}
              <Paper sx={{ p: 3, borderRadius: 3, mb: 2 }} elevation={0}>
                <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Gavel color="primary" /> Make an Offer
                </Typography>
                {isAuthenticated ? (
                  <>
                    <TextField
                      fullWidth label="Your Offer" type="number" sx={{ mb: 2 }}
                      value={offerPrice} onChange={(e) => setOfferPrice(e.target.value)}
                      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
                      placeholder={`Asking: ₹${item.selling_price?.toLocaleString()}`}
                    />
                    <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                      <Button variant="contained" fullWidth onClick={handleOffer} startIcon={<Gavel />}>
                        Submit Offer
                      </Button>
                      <Button variant="outlined" fullWidth onClick={handleNegotiate} startIcon={<AutoAwesome />}>
                        AI Negotiate
                      </Button>
                    </Box>
                    {negotiation && (
                      <Alert severity="info" sx={{ borderRadius: 2 }} icon={<AutoAwesome />}>
                        <Typography variant="subtitle2" fontWeight={600}>AI Settlement Suggestion</Typography>
                        <Typography variant="h6" color="primary" fontWeight={700}>
                          ₹{negotiation.suggested_settlement?.toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ mt: 0.5 }}>{negotiation.reasoning}</Typography>
                      </Alert>
                    )}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary" textAlign="center">
                    Login to negotiate
                  </Typography>
                )}
              </Paper>

              {/* Recommendations */}
              {recommendations.length > 0 && (
                <Paper sx={{ p: 3, borderRadius: 3 }} elevation={0}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>Similar Items</Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                    {recommendations.map((rec) => (
                      <Card key={rec.id} variant="outlined" sx={{
                        borderRadius: 2, '&:hover': { borderColor: 'primary.main', bgcolor: '#f8fafc' },
                      }}>
                        <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 }, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <Box>
                            <Typography variant="body2" fontWeight={500}>{rec.item_name}</Typography>
                            <Typography variant="caption" color="text.secondary">{rec.location}</Typography>
                          </Box>
                          <Typography variant="body2" color="primary.main" fontWeight={600}>
                            ₹{rec.selling_price?.toLocaleString()}
                          </Typography>
                        </CardContent>
                      </Card>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Message Dialog */}
        <Dialog open={messageDialog} onClose={() => setMessageDialog(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Message Seller</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Ask about: {item?.item_name}
            </Typography>
            <TextField
              fullWidth multiline rows={4} placeholder="Hi, I'm interested in this item..."
              value={messageText} onChange={(e) => setMessageText(e.target.value)}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setMessageDialog(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleSendMessage} startIcon={<Send />}>Send</Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
};

export default ItemDetailPage;
